import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { ArrowsLeftRight, ChatCircle, VideoCamera } from "@phosphor-icons/react"
import { PartnerTypeAppointmentTags } from "@/components/appointment-tags"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { TextWithTooltip } from "@/components/text-with-tooltip"
import { Tag } from "@/components/ui/tag"
import { UserProvider } from "@/components/user-wrapper"
import { ageFormat } from "@/lib/age-format"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { cn } from "@/lib/utils"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import { appointmentRow_WaitingRoomAppointment$key } from "@/modules/waiting-room/components/appointment-table/__generated__/appointmentRow_WaitingRoomAppointment.graphql"
import { appointmentRowPatient_WaitingRoomAppointment$key } from "@/modules/waiting-room/components/appointment-table/__generated__/appointmentRowPatient_WaitingRoomAppointment.graphql"
import { WaitingRoomAppointment } from "@/modules/waiting-room/components/appointment-table/appointment-item-content"
import { AppointmentPriority } from "@/modules/waiting-room/components/appointment-table/appointment-priority"
import { AppointmentServiceLevelAgreement } from "@/modules/waiting-room/components/appointment-table/appointment-service-level-agreement"
import { AppointmentStatusCell } from "@/modules/waiting-room/components/appointment-table/appointment-status-cell"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import { delayFormat } from "@/modules/waiting-room/helpers/delay-format"
import { DaylightSavingTimeChange } from "@/types/__generated__/union-types"

const WaitingRoomAppointmentPatientFragment = graphql`
  fragment appointmentRowPatient_WaitingRoomAppointment on WaitingRoomAppointment {
    appointment {
      patient {
        displayId
        gender
        age {
          days
          months
          weeks
          years
        }
      }
    }
  }
`

const WaitingRoomAppointmentDataFragment = graphql`
  fragment appointmentRow_WaitingRoomAppointment on WaitingRoomAppointment {
    id
    flexibility
    ...appointmentServiceLevelAgreementFragment
    appointment {
      id
      delay
      isCompleted
      isPartnerInVideoCall
      channel
      urgency
      assignedTo
      reason
      access
      service
      specialty
      language
      appointmentType
      tags {
        variant
        value
      }
      medgateConnectOrder {
        status
      }
      fileUploadRequest {
        status
        createdAt
        createdBy
        type
      }
      patient {
        patientFileLock {
          lockedBy
        }
      }
      ...appointmentStatusCell_appointment
    }
  }
`

interface AppointmentRowProps extends React.ComponentProps<"div"> {
  waitingRoomAppointment: WaitingRoomAppointment
  displaySlot?: string
  daylightSavingTimeChange: DaylightSavingTimeChange | undefined
}

export const AppointmentRow = ({
  waitingRoomAppointment,
  displaySlot,
  className,
  daylightSavingTimeChange,
  ...restProps
}: AppointmentRowProps) => {
  const { t } = useTranslation()

  const selectedWaitingRoomAppointmentId = useAppointmentContext((state) => state.selectedWaitingRoomAppointmentId)

  const {
    appointment: {
      patient: { age, displayId, gender },
    },
  } = useFragment<appointmentRowPatient_WaitingRoomAppointment$key>(
    WaitingRoomAppointmentPatientFragment,
    waitingRoomAppointment,
  )

  const data = useFragment<appointmentRow_WaitingRoomAppointment$key>(
    WaitingRoomAppointmentDataFragment,
    waitingRoomAppointment,
  )

  const { id: waitingRoomId, flexibility, appointment } = data

  const {
    id,
    delay,
    isCompleted,
    isPartnerInVideoCall,
    channel,
    urgency,
    assignedTo,
    reason,
    access,
    service,
    specialty,
    language,
    appointmentType,
    patient: {
      patientFileLock: { lockedBy },
    },
  } = appointment

  const isMedgateConnectWaitingForDocuments =
    isMedgateConnect(service) && appointmentType === "FOLLOW_UP" && appointment.medgateConnectOrder?.status === "OPEN"

  return (
    <div
      id={waitingRoomId}
      key={id}
      data-state={selectedWaitingRoomAppointmentId === waitingRoomId ? "selected" : false}
      className={cn(
        "border-border relative col-span-full grid h-10 cursor-pointer items-center gap-4 px-4 py-2 text-sm",
        isCompleted || lockedBy
          ? "bg-surface-disabled"
          : "text-on-surface hover:bg-surface-hovered active:bg-surface-pressed",
        !isCompleted && "appointment-not-closed",
        urgency !== "NORMAL" && "appointment-urgent-acute",
        className,
      )}
      {...restProps}>
      <div data-testid="slot-cell" className="grid grid-cols-2 items-center justify-start gap-1 truncate">
        <div>
          <div>{displaySlot}</div>
          {daylightSavingTimeChange && daylightSavingTimeChange !== "NONE" && (
            <div className="text-on-warning-surface m-0 p-0 text-[10px]">
              {t("scheduling.waitingRoom.daylightSavingTimeChange.switchTime")}
            </div>
          )}
        </div>
        {delay > 0 && (
          <Tag
            className="max-w-full"
            data-testid="delay-time"
            type="warning"
            hasIcon={false}
            label={delayFormat(delay)}
          />
        )}
      </div>

      <AppointmentServiceLevelAgreement data-testid="sla-cell" serviceLevelAgreementThresholdsFragment={data} />

      <AppointmentPriority
        className="truncate"
        data-testid="priority-cell"
        urgency={urgency}
        flexibility={flexibility}
      />

      <div className="truncate" data-testid="assigned-cell">
        {assignedTo && (
          <UserProvider
            userName={assignedTo}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={id} />}
          />
        )}
      </div>

      <div className="truncate" data-testid="language-cell">
        {convertLanguageToISOCode(language)}
      </div>

      <div className="flex items-center gap-1 truncate" data-testid="age-cell">
        <GenderIcon className="h-6 w-6 shrink-0" size={18} gender={gender} />
        {age && <span>{ageFormat({ age })}</span>}
      </div>

      <div className="truncate" data-testid="displayId-cell">
        {displayId}
      </div>

      {channel === "VIDEO" && isPartnerInVideoCall ? (
        <div data-testid="channel-cell" className="relative flex items-center">
          <span className="bg-success/50 pointer-events-none absolute size-5 animate-ping rounded-full" />
          <VideoCamera className="text-success relative" weight="fill" />
        </div>
      ) : (
        <div data-testid="channel-cell" className="channel">
          {channel === "VIDEO" && <VideoCamera className="text-on-surface-subdued" />}
          {channel === "CHAT" && <ChatCircle className="text-on-surface-subdued" />}
          {channel === "ASYNC_REQUEST" && <ArrowsLeftRight className="text-on-surface-subdued" />}
        </div>
      )}

      <TextWithTooltip data-testid="access-cell">
        {(access === "BOOK_DOC_AT_PHARMACY" ||
          access === "BOOK_DOC_AT_PARTNER" ||
          access === "PARTNER_PORTAL" ||
          access === "WEB_BOOKING") &&
          t(`scheduling.waitingRoom.access.${access}`)}
      </TextWithTooltip>

      <TextWithTooltip data-testid="service-cell">{t(`scheduling.waitingRoom.service.${service}`)}</TextWithTooltip>

      <TextWithTooltip data-testid="specialty-cell">
        {t(`scheduling.waitingRoom.serviceSpecialty.${specialty}`)}
      </TextWithTooltip>

      <div data-testid="reason-cell" className="grid grid-flow-col justify-start gap-1 truncate">
        <PartnerTypeAppointmentTags tags={appointment.tags} />
        {appointmentType === "FOLLOW_UP" && (
          <Tag
            data-testid="appointment-tag-follow-up"
            type="neutral"
            label={t("scheduling.waitingRoom.appointmentType.FOLLOW_UP")}
          />
        )}
        {(appointment.fileUploadRequest?.status === "PENDING" || isMedgateConnectWaitingForDocuments) && (
          <Tag
            data-testid="appointment-tag-wait-for-document"
            type="warning"
            label={t("scheduling.waitingRoom.requestDocument.waitingForDocument")}
          />
        )}
        <div className="truncate">{reason}</div>
      </div>

      <div className="truncate" data-testid="status-cell">
        <AppointmentStatusCell appointmentRef={appointment} />
      </div>
    </div>
  )
}
